import { Plus } from 'assets/icons';
import CountBadge from 'components/badge/CountBadge';
import useFlipLayout from 'hooks/useFlipLayout';
import useText from 'hooks/useText';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import TaskKanbanCard from './TaskKanbanCard';

const COLUMN_LABELS = {
  late: 'groupLate',
  today: 'groupToday',
  tomorrow: 'groupTomorrow',
  later: 'groupLater',
  done: 'groupDone',
};

/**
 * The tasks by due day, then the last ones done. A card dropped on a column
 * gets its due day, or is done; nothing is dropped on « En retard ». Under the
 * cards of a column, a card adds a task there. The cards glide to their place
 * (hooks/useFlipLayout, as the vivier board): the page calls `capture()`
 * through the ref right before its data changes.
 */
const TaskKanban = forwardRef(
  ({ columns, order, team, meId, onMove, onOpen, onAssign, onAdd }, ref) => {
    const { text } = useText('tasks');
    const board = useRef(null);
    const grab = useRef(null);
    const { capture } = useFlipLayout(board, { keyAttribute: 'data-id' });
    const [dragged, setDragged] = useState(null);
    const [over, setOver] = useState(null);
    useImperativeHandle(ref, () => ({ capture }), [capture]);

    const startDrag = (event, row, column) => {
      // Firefox drags nothing without data.
      event.dataTransfer?.setData('text/plain', row.key);
      const rect = event.currentTarget.getBoundingClientRect();
      grab.current = { key: row.key, x: event.clientX - rect.left, y: event.clientY - rect.top };
      setDragged({ row, column });
    };
    // The dropped card starts its glide where it was let go, not from its
    // old column. Some browsers report a drop at (0, 0): old place then.
    const dropOrigin = (event) => {
      const held = grab.current;
      grab.current = null;
      if (!held || (!event.clientX && !event.clientY)) return {};
      return { [held.key]: { left: event.clientX - held.x, top: event.clientY - held.y } };
    };
    const drop = (event, column) => {
      event.preventDefault();
      setOver(null);
      if (dragged && column !== 'late' && column !== dragged.column) {
        capture(dropOrigin(event));
        onMove(dragged.row, column);
      }
      setDragged(null);
    };

    return (
      <div ref={board} className="flex h-full min-h-0 gap-4 overflow-x-auto px-4 py-5 lg:px-8">
        {order.map((column) => {
          const cards = columns[column] ?? [];
          const droppable = dragged && column !== 'late' && dragged.column !== column;
          return (
            <section
              key={column}
              onDragOver={(event) => {
                if (!droppable) return;
                event.preventDefault();
                setOver(column);
              }}
              onDragLeave={() => setOver((current) => (current === column ? null : current))}
              onDrop={(event) => drop(event, column)}
              className={`flex min-h-0 w-72 shrink-0 flex-col rounded-xl border bg-[#fcfcfc] transition-colors ${
                over === column ? 'border-yellow-400 bg-yellow-50' : 'border-gray-100'
              }`}
            >
              <header className="flex items-center gap-2 px-3 py-3">
                <p className="text-xs font-medium uppercase text-gray-700">{text(COLUMN_LABELS[column])}</p>
                <CountBadge value={cards.length} variant="outline" />
              </header>
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-3">
                {cards.map((row) => (
                  <TaskKanbanCard
                    key={row.key}
                    row={row}
                    dragged={dragged?.row.key === row.key}
                    team={team}
                    meId={meId}
                    onDragStart={(event) => startDrag(event, row, column)}
                    onDragEnd={() => {
                      setDragged(null);
                      setOver(null);
                    }}
                    onOpen={onOpen}
                    onAssign={onAssign}
                  />
                ))}
                {!cards.length && droppable && (
                  <p className="px-2 py-6 text-center text-xs text-gray-400">{text('kanbanDrop')}</p>
                )}
                {column !== 'late' && (
                  <button
                    type="button"
                    onClick={() => onAdd(column)}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 px-3 py-2.5 text-sm font-normal text-gray-400 transition hover:border-gray-300 hover:bg-white hover:text-gray-600"
                  >
                    <Plus wh={16} color="currentColor" />
                    {text('kanbanAdd')}
                  </button>
                )}
              </div>
            </section>
          );
        })}
      </div>
    );
  },
);

export default TaskKanban;
